/**
 * PartnershipAttributeForm - renders a tenant-defined EAV form.
 *
 * Given the `attribute_values` list the backend returns on the partnership
 * detail endpoint, renders one field per attribute driven by `field_type`
 * and `configuration_data`. Save diffs against the original values and
 * only sends changed keys via PATCH.
 *
 * Supported field types (first pass - extend as more tenants need them):
 *   SELECT, MULTI_SELECT, TEXT, EMAIL, PHONE, NUMBER, BOOLEAN
 * Unknown types fall through to a plain text input so we never hard-fail
 * on a new field_type added to the EAV vocabulary.
 */

import { useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { AddressCapture } from '@/shared/components/ui/address-capture'
import { AddressLookupProvider } from '@/shared/components/ui/address-lookup-context'
import {
  toAddressInputValue,
  toMailingAddress,
  isMeaningfulMailingAddress,
} from '@/shared/lib/formatting/address'
import type { PartnershipAttributeValue } from '@/admin/types/organisations'
import { useUpdatePartnershipAttributeValues } from '@/admin/hooks/use-partnership-attribute-values'
import { createAddressLookupApi } from '@/admin/hooks/use-lookups'
import { useQueryClient } from '@tanstack/react-query'

interface Props {
  partnershipUuid: string
  attributeValues: PartnershipAttributeValue[]
  onSaved?: () => void
  // Section heading. Defaults to "Partner profile" for backwards
  // compatibility. Pages that split attributes across multiple forms
  // (e.g. pulling ASM contact fields into their own card) use this to
  // label each section distinctly.
  title?: string
  // When set, only attributes whose ``code_name`` is in this list are
  // rendered. Useful for carving out a focused sub-form (ASM details)
  // without duplicating the form shell.
  includeCodes?: readonly string[]
  // When set, attributes whose ``code_name`` is in this list are
  // hidden. Complements ``includeCodes`` for the "everything else"
  // form.
  excludeCodes?: readonly string[]
}

function normalise(value: unknown): unknown {
  // Treat empty strings as "unset" so the PATCH body doesn't bother
  // the backend with trivial no-ops.
  if (value === '') return null
  return value
}

function readOption(config: Record<string, unknown>): string[] {
  const options = config?.options
  if (!Array.isArray(options)) return []
  return options.filter((o): o is string => typeof o === 'string')
}

export function PartnershipAttributeForm({
  partnershipUuid,
  attributeValues,
  onSaved,
  title = 'Partner profile',
  includeCodes,
  excludeCodes,
}: Props) {
  // Apply include/exclude filtering up-front so every subsequent
  // memo/handler works against the visible subset only.
  const visibleAttributes = useMemo(() => {
    let list = attributeValues
    if (includeCodes && includeCodes.length > 0) {
      const set = new Set(includeCodes)
      list = list.filter((a) => set.has(a.code_name))
    }
    if (excludeCodes && excludeCodes.length > 0) {
      const set = new Set(excludeCodes)
      list = list.filter((a) => !set.has(a.code_name))
    }
    return list
  }, [attributeValues, includeCodes, excludeCodes])

  // Local edit state keyed by code_name. Initialised from the server's
  // current values so the form is controlled from the first render.
  const [draft, setDraft] = useState<Record<string, unknown>>(() => {
    const seed: Record<string, unknown> = {}
    for (const a of visibleAttributes) seed[a.code_name] = a.value ?? ''
    return seed
  })

  const mutation = useUpdatePartnershipAttributeValues(partnershipUuid)

  // Lookup API for any editable ADDRESS field (postcode search + autofill).
  // Built once; AddressCapture reads it off the context provider below.
  const queryClient = useQueryClient()
  const addressLookupApi = useMemo(
    () => createAddressLookupApi(queryClient),
    [queryClient],
  )

  // What changed vs. the server state - drives the Save button's
  // disabled state and the PATCH payload.
  const changed = useMemo(() => {
    const out: Record<string, unknown> = {}
    for (const a of visibleAttributes) {
      const current = a.value ?? ''
      const next = draft[a.code_name] ?? ''
      if (JSON.stringify(current) !== JSON.stringify(next)) {
        out[a.code_name] = normalise(next)
      }
    }
    return out
  }, [visibleAttributes, draft])

  const hasChanges = Object.keys(changed).length > 0

  const handleSave = async () => {
    try {
      await mutation.mutateAsync(changed)
      toast.success(`${title} updated.`)
      onSaved?.()
    } catch (err: unknown) {
      const response =
        typeof err === 'object' && err !== null && 'response' in err
          ? (err as { response?: { data?: { result?: unknown; message?: unknown } } })
              .response
          : undefined
      const detail = response?.data?.result ?? response?.data?.message
      toast.error(
        typeof detail === 'string' ? detail : `Could not save ${title.toLowerCase()}.`,
      )
    }
  }

  const handleReset = () => {
    const reset: Record<string, unknown> = {}
    for (const a of visibleAttributes) reset[a.code_name] = a.value ?? ''
    setDraft(reset)
  }

  if (visibleAttributes.length === 0) return null

  return (
    <AddressLookupProvider api={addressLookupApi}>
    <section className="mt-8 border-t-[0.5px] border-t-[var(--rule)] pt-8">
      <div className="mb-6 flex items-baseline justify-between">
        <h3 className="clara-section-label">{title}</h3>
        <div className="flex gap-2">
          {hasChanges && (
            <Button
              variant="ghost"
              onClick={handleReset}
              disabled={mutation.isPending}
              className="px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] rounded-[4px]"
            >
              Discard
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={!hasChanges || mutation.isPending}
            className="px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] rounded-[4px] bg-[var(--green)] text-[var(--bg)] border-none hover:bg-[var(--green-hover)] disabled:opacity-50"
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                Saving
              </>
            ) : (
              'Save changes'
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5 max-w-[720px]">
        {visibleAttributes.map((attr) => (
          <AttributeField
            key={attr.code_name}
            attr={attr}
            value={draft[attr.code_name]}
            onChange={(v) =>
              setDraft((prev) => ({ ...prev, [attr.code_name]: v }))
            }
          />
        ))}
      </div>
    </section>
    </AddressLookupProvider>
  )
}

interface FieldProps {
  attr: PartnershipAttributeValue
  value: unknown
  onChange: (v: unknown) => void
}

function AttributeField({ attr, value, onChange }: FieldProps) {
  const fieldId = `attr-${attr.code_name}`
  const disabled = !attr.editable
  const options = readOption(attr.configuration_data)

  const label = (
    <Label
      htmlFor={fieldId}
      className="mb-1.5 block text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
    >
      {attr.display_name}
      {attr.required && (
        <span className="ml-1 text-[var(--status-error)]">*</span>
      )}
    </Label>
  )

  switch (attr.field_type) {
    case 'SELECT':
      return (
        <div>
          {label}
          <Select
            value={typeof value === 'string' ? value : ''}
            onValueChange={(v) => onChange(v)}
            disabled={disabled}
          >
            <SelectTrigger id={fieldId} className="w-full">
              <SelectValue placeholder="-" />
            </SelectTrigger>
            <SelectContent>
              {options.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )

    case 'MULTI_SELECT': {
      const selected = Array.isArray(value) ? (value as string[]) : []
      return (
        <div>
          {label}
          <div className="flex flex-col gap-1.5">
            {options.map((opt) => {
              const checked = selected.includes(opt)
              return (
                <label
                  key={opt}
                  className="flex items-center gap-2 text-[13px] text-[var(--ink)] cursor-pointer"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(next) => {
                      const nextSet = new Set(selected)
                      if (next) nextSet.add(opt)
                      else nextSet.delete(opt)
                      onChange(Array.from(nextSet))
                    }}
                    disabled={disabled}
                  />
                  {opt}
                </label>
              )
            })}
          </div>
        </div>
      )
    }

    case 'BOOLEAN':
      return (
        <div>
          {label}
          <label className="flex items-center gap-2 text-[13px] text-[var(--ink)] cursor-pointer">
            <Checkbox
              checked={value === true}
              onCheckedChange={(v) => onChange(Boolean(v))}
              disabled={disabled}
            />
            {attr.description || 'Enabled'}
          </label>
        </div>
      )

    case 'NUMBER':
      return (
        <div>
          {label}
          <Input
            id={fieldId}
            type="number"
            value={typeof value === 'number' || typeof value === 'string' ? String(value) : ''}
            onChange={(e) => {
              const raw = e.target.value
              onChange(raw === '' ? null : Number(raw))
            }}
            disabled={disabled}
          />
        </div>
      )

    case 'EMAIL':
      return (
        <div>
          {label}
          <Input
            id={fieldId}
            type="email"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
        </div>
      )

    case 'PHONE':
      return (
        <div>
          {label}
          <Input
            id={fieldId}
            type="tel"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
        </div>
      )

    case 'ADDRESS': {
      // Composite address stored as a camelCase dict (addressLine1, locality,
      // region, postalCode, countryCode).
      if (!disabled) {
        // Editable: the platform address widget (postcode search + manual
        // entry), mirroring AddressMetadataField. The stored value stays the
        // backend camelCase shape; AddressCapture works in snake_case
        // MailingAddress, so convert on the way in and out. The lookup runs off
        // the AddressLookupProvider wrapping this form.
        const current = toMailingAddress(value)
        return (
          // Full width: AddressCapture expands into several fields in manual
          // entry, which is cramped in a half-width grid column.
          <div className="md:col-span-2">
            {label}
            <AddressCapture
              value={isMeaningfulMailingAddress(current) ? current : null}
              onChange={(next) =>
                onChange(next ? toAddressInputValue(next) : null)
              }
              summaryLabel=""
            />
          </div>
        )
      }
      // Read-only: source-populated fields are shown, never hand-typed.
      const addr =
        value && typeof value === 'object'
          ? (value as Record<string, string>)
          : {}
      const parts = [
        addr.addressLine1,
        addr.addressLine2,
        addr.locality,
        addr.region,
        addr.postalCode,
        addr.countryCode,
      ].filter((p) => typeof p === 'string' && p.trim() !== '')
      return (
        <div>
          {label}
          <div className="min-h-[38px] w-full rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-3 py-2 text-[13px] text-[var(--ink)]">
            {parts.length > 0 ? (
              parts.join(', ')
            ) : (
              <span className="text-[var(--ink-3)]">-</span>
            )}
          </div>
        </div>
      )
    }

    case 'TEXT':
    default:
      return (
        <div>
          {label}
          <Input
            id={fieldId}
            type="text"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
        </div>
      )
  }
}
